'use client';
import useSWR from 'swr';
import Details from './details';
import ProductTabs from './tabs';
import Container from '../ui/container';
import { useEffect, useMemo, useState } from 'react';
import RelatedProducts from './related-products';
// import ProductBreadCrumb from './ProductBreadCrumb';
import ProductCollections from './details/collections';
import { useLocale, useTranslations } from 'next-intl';
import { getProductsBySkusV2 } from '@/services/product/related-product-info';
import { event_view_item } from '@/utils/gtm';
import Breadcrumb from '../ui/breadcrumb';
import { getProductReviewsV2 } from '@/services/product';

const ProductWrapper = ({ product }) => {
  const locale = useLocale();
  const t = useTranslations('product');
  const [ratings, setRatings] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [upSellProducts, setUpSellProducts] = useState([]);
  const [crossSellProducts, setCrossSellProducts] = useState([]);

  const bc_categories = useMemo(() => {
    if (!product || !product.category) [];
    const cats = [product?.category?.at(1)];
    return cats
      .map((cat) => ({
        name: cat?.name,
        url: cat?.url_key
          ? `${cat?.url_key}-c-${cat?.category_id}`
          : `${cat?.name?.replaceAll(' ', '-')?.toLowerCase()}-c-${cat?.category_id}`,
      }))
      .filter(({ name }) => name);
  }, [product]);

  const relatedSkus = product?.custom_related_products || [];
  const crossSellSkus = product?.cross_sell_products || [];
  const upsellSkus = product?.up_sell_products || [];
  const allSkus = [
    ...new Set([...relatedSkus, ...crossSellSkus, ...upsellSkus]),
  ];

  useEffect(() => {
    event_view_item(product);
  }, [product]);

  useSWR(
    () => (allSkus?.length > 0 ? `${locale}-${allSkus.toString()}` : null),
    async () => await getProductsBySkusV2({ skuBulk: allSkus, locale }),
    {
      revalidateOnFocus: false,
      onSuccess: ({ data }) => {
        const products = data?.data?.products;
        const related = products?.filter((p) => relatedSkus.includes(p.sku));
        const crossSell = products?.filter((p) =>
          crossSellSkus.includes(p.sku)
        );
        const upSell = products?.filter((p) => upsellSkus.includes(p.sku));
        setRelatedProducts(related);
        setCrossSellProducts(crossSell);
        setUpSellProducts(upSell);
      },
    }
  );

  // fetch product ratings
  useSWR(
    () => (product?.sku ? `productRatings-${product?.sku}/${locale}` : null),
    () => getProductReviewsV2({ sku: product?.sku, locale }),
    {
      revalidateOnMount: 30 * 60 * 1000,
      revalidateOnFocus: false,
      onSuccess: ({ data }) => {
        setRatings(data?.data);
      },
    }
  );

  return (
    <>
      <Breadcrumb current={product?.name} parentCategory={bc_categories} />
      <Container className='mt-[20px] mb-[34px] flex flex-col gap-4 lg:gap-8'>
        <div className='grid gap-[27px] max-sm:gap-2.5 lg:grid-cols-2'>
          {/* Prices, Configurable, Stock, Offers, buy btn, add to cart btn */}
          <Details product={product} parentCategory={bc_categories} />
        </div>

        {/* TODO: Connect this with dynamic data from the backend */}
        <ProductCollections
          ids={[
            product?._category_?.main_category?.category_id,
            ...product?._category_?.sub_categories.map(
              (info) => info?.category_id
            ),
          ]}
        />

        {/* Product Tabs done */}
        <ProductTabs
          specification={product?.product_attributes}
          description={product?.description}
          reviews={ratings}
        />
        {/* Products Slider done */}
        {[
          { products: relatedProducts, title: t('up_sell_products_key') },
          { products: upSellProducts, title: t('related_products_key') },
          { products: crossSellProducts, title: t('also_viewed_key') },
        ]?.map((item, key) => (
          <RelatedProducts
            key={key}
            isLoading={false}
            relatedProducts={item?.products}
            title={item?.title}
          />
        ))}
      </Container>
    </>
  );
};

export default ProductWrapper;
